import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Divider from "@mui/material/Divider"
import Fade from "@mui/material/Fade"
import FormControl from "@mui/material/FormControl"
import IconButton from "@mui/material/IconButton"
import InputLabel from "@mui/material/InputLabel"
import LinearProgress from "@mui/material/LinearProgress"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { capitalize } from "lodash"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { selectFullName, selectProfile } from "../../users/reducer"
import {
  BusinessInfoFields,
  EMPTY_BUSINESS,
  EMPTY_SOLE_PROP,
  SolePropFields,
  businessErrors,
  businessPayload,
  solePropErrors,
  solePropPayload,
} from "../a2p/A2PFields"
import A2PFormErrors, { a2pErrorMessages } from "../a2p/A2PFormErrors"
import { SOLE_PROPRIETOR } from "../a2p/a2pStatus"
import { selectCRMSettings, updateCRMSettings } from "../reducer"
import { A2P_10DLC_REG_STAGES, useValidatedPhoneNumber } from "../utils"
import DialogHeader from "./DialogHeader"

const STANDARD_PATH = "standard"
const SOLE_PROP_PATH = "sole_prop"

export default function RegisterA2P10DLCDialog({ open, onClose }) {
  const [stepIndex, setStepIndex] = useState(0)
  const crmSettings = useSelector(selectCRMSettings)
  const regStage = crmSettings.settings.twilio_a2p_10dlc_reg_stage
  const brandType = crmSettings.settings.a2p_status?.brand_type
  const [path, setPath] = useState(null)
  let effectivePath = path || STANDARD_PATH
  if (!path && regStage && regStage !== A2P_10DLC_REG_STAGES.not_attempted) {
    effectivePath =
      brandType === SOLE_PROPRIETOR ? SOLE_PROP_PATH : STANDARD_PATH
  }
  const steps =
    effectivePath === SOLE_PROP_PATH ? SOLE_PROP_STEPS : STANDARD_STEPS

  function handleSoftClose(closeReason) {
    if (
      Object.values(REGISTER_A2P_10DLC_DIALOG_CLOSE_REASONS).indexOf(
        closeReason
      ) > -1
    ) {
      onClose(closeReason)
    } else {
      onClose()
    }
  }

  useEffect(() => {
    const index = stepIndexForStage(regStage, effectivePath)
    if (index !== null) {
      setStepIndex(index)
    }
  }, [regStage, effectivePath])

  const safeIndex = Math.min(stepIndex, steps.length - 1)
  const StepComponent = steps[safeIndex].component

  return (
    <Dialog open={open} onClose={handleSoftClose} maxWidth="sm" fullWidth>
      <DialogHeader
        title={
          <Row sx={{ gap: 1 }}>
            <Typography variant="h3">{`${steps[safeIndex].title} (${
              safeIndex + 1
            }/${steps.length})`}</Typography>
          </Row>
        }
        onClose={handleSoftClose}
      />
      <LinearProgress
        variant="determinate"
        color="primary"
        value={(100 * safeIndex) / steps.length}
      />
      <StepComponent
        setStepIndex={setStepIndex}
        onClose={handleSoftClose}
        setPath={setPath}
      />
    </Dialog>
  )
}

function stepIndexForStage(regStage, path) {
  const stages = A2P_10DLC_REG_STAGES
  if (path === SOLE_PROP_PATH) {
    switch (regStage) {
      case stages.end_user_business_info:
      case stages.end_user_repr_info:
      case stages.customer_profile_review_pending:
        return 2
      case stages.trust_product_approved:
        return 3
      case stages.brand_registration_sent:
        return 4
      default:
        return null
    }
  }
  switch (regStage) {
    case stages.end_user_business_info:
      return 3
    case stages.end_user_repr_info:
      return 4
    case stages.customer_profile_review_pending:
      return 5
    case stages.trust_product_approved:
      return 6
    case stages.brand_registration_sent:
      return 7
    default:
      return null
  }
}

function IntroStep({ setStepIndex, onClose }) {
  return (
    <StepWrapper>
      <DialogContent>
        <Column sx={{ gap: 2 }}>
          <Typography variant="body1">
            <strong>
              A2P (Application to Person) 10DLC (10 digit long code)
            </strong>{" "}
            is the standard that United States telecom carriers have put in
            place to ensure that SMS traffic to US end-users through long code
            phone numbers is verified and consensual.
          </Typography>
          <Typography variant="body1">
            In this wizard, we'll guide you through registering your business
            and getting your own texting number, so your texts to candidates
            aren't blocked by carriers. Carriers check your details against
            official records, so have your IRS EIN letter (CP 575 or 147C) handy
            if you have one.
          </Typography>
          <Typography variant="body1">
            Click <strong>Let's get started!</strong> below to get started.
          </Typography>
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Not now
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setStepIndex((stepIndex) => stepIndex + 1)}
        >
          Let's get started!
        </Button>
      </DialogActions>
    </StepWrapper>
  )
}

function EinQuestionStep({ setStepIndex, setPath, onClose }) {
  function choose(path) {
    setPath(path)
    setStepIndex(2)
  }
  return (
    <StepWrapper>
      <DialogContent>
        <Column sx={{ gap: 2 }}>
          <Typography variant="h5">
            Do you have your own business EIN (not your brokerage&apos;s)?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            An EIN is the 9-digit tax ID the IRS issues to a business, such as
            your LLC or corporation. Your brokerage&apos;s EIN can&apos;t be
            used for your own registration.
          </Typography>
          <Typography variant="body1" color="text.secondary">
            No EIN? You can register as a <strong>sole proprietor</strong> with
            your name and mobile number instead (one texting number, lower daily
            volume).
          </Typography>
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="primary" onClick={onClose}>
          {STOP_TEXT}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => choose(SOLE_PROP_PATH)}
        >
          No, I don&apos;t
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => choose(STANDARD_PATH)}
        >
          Yes, I have my own EIN
        </Button>
      </DialogActions>
    </StepWrapper>
  )
}

function SolePropStep({ setStepIndex, onClose }) {
  const profile = useSelector(selectProfile)
  const [data, setData] = useState(EMPTY_SOLE_PROP)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState([])
  const axios = useAuthorizedAxiosClient()
  const updateCRMSettingsAndGoNext = useUpdateCRMSettingsAndGoNext()
  const formErrors = solePropErrors(data)

  useEffect(
    () => {
      setData((current) => ({
        ...current,
        first_name: current.first_name || profile?.first_name || "",
        last_name: current.last_name || profile?.last_name || "",
        email: current.email || profile?.email || "",
        mobile_phone_number:
          current.mobile_phone_number || profile?.phone || "",
      }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile]
  )

  function handleNextButtonClicked() {
    setIsLoading(true)
    setErrors([])
    axios
      .post(
        "/crm/api/twilio/a2p-10dlc/sole-prop/1-profile/",
        solePropPayload(data)
      )
      .then(({ data: responseData }) =>
        updateCRMSettingsAndGoNext(setStepIndex, responseData)
      )
      .catch((error) => setErrors(twilioOrApiErrors(error)))
      .finally(() => setIsLoading(false))
  }

  return (
    <StepWrapper>
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Register as a sole proprietor with your own details. Carriers allow
            one texting number per sole proprietor, and will text your mobile a
            confirmation request after you submit.
          </Typography>
          <SolePropFields value={data} onChange={setData} />
          <A2PFormErrors messages={errors} />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          {STOP_TEXT}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isLoading}
          onClick={handleNextButtonClicked}
          disabled={Object.keys(formErrors).length > 0}
        >
          Submit and register
        </LoadingButton>
      </DialogActions>
    </StepWrapper>
  )
}

function InitiateSecondaryCustomerProfileStep({ setStepIndex, onClose }) {
  const [business, setBusiness] = useState(EMPTY_BUSINESS)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState([])
  const axios = useAuthorizedAxiosClient()
  const updateCRMSettingsAndGoNext = useUpdateCRMSettingsAndGoNext()
  const formErrors = businessErrors(business)

  function handleNextButtonClicked() {
    setIsLoading(true)
    setErrors([])
    axios
      .post("/crm/api/twilio/a2p-10dlc/1-initiate/", businessPayload(business))
      .then(({ data }) => {
        updateCRMSettingsAndGoNext(setStepIndex, data)
      })
      .catch((error) => setErrors(a2pErrorMessages(error)))
      .finally(() => setIsLoading(false))
  }

  return (
    <StepWrapper>
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Carriers check these details against IRS records. The most common
            reason for a rejection is a legal name or EIN that doesn&apos;t
            match the IRS letter exactly.
          </Typography>
          <BusinessInfoFields value={business} onChange={setBusiness} />
          <A2PFormErrors messages={errors} />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          {STOP_TEXT}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isLoading}
          onClick={handleNextButtonClicked}
          disabled={Object.keys(formErrors).length > 0}
        >
          Set up business rep. info
        </LoadingButton>
      </DialogActions>
    </StepWrapper>
  )
}

function RepresentativeEndUserStep({ setStepIndex, onClose }) {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const { phone, phoneError, handlePhoneChanged } =
    useValidatedPhoneNumber(true)
  const [jobPosition, setJobPosition] = useState("")
  const [businessTitle, setBusinessTitle] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [apiErrors, setApiErrors] = useState([])
  const profile = useSelector(selectProfile)
  const axios = useAuthorizedAxiosClient()
  const updateCRMSettingsAndGoNext = useUpdateCRMSettingsAndGoNext()

  function handleNextButtonClicked() {
    setIsLoading(true)
    axios
      .post("/crm/api/twilio/a2p-10dlc/2-repr-end-user/", {
        first_name: firstName,
        last_name: lastName,
        email,
        phone_number: phone,
        job_position: jobPosition,
        business_title: businessTitle,
      })
      .then(({ data }) => updateCRMSettingsAndGoNext(setStepIndex, data))
      .catch((error) => setApiErrors(a2pErrorMessages(error)))
      .finally(() => setIsLoading(false))
  }

  useEffect(
    () => {
      setFirstName(profile.first_name)
      setLastName(profile.last_name)
      setEmail(profile.email)
      if (profile.phone) {
        handlePhoneChanged(profile.phone)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile]
  )

  return (
    <StepWrapper>
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <TextField
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
            fullWidth
          />
          <TextField
            label="Phone number"
            value={phone}
            onChange={(e) => handlePhoneChanged(e.target.value)}
            type="tel"
            error={!!phoneError}
            helperText={phoneError}
            required
            fullWidth
          />
          <FormControl fullWidth required size="small">
            <InputLabel id="label-job-pos">Job position</InputLabel>
            <Select
              value={jobPosition}
              onChange={(e) => setJobPosition(e.target.value)}
              labelId="label-job-pos"
            >
              {[
                "Director",
                "GM",
                "VP",
                "CEO",
                "CFO",
                "General Counsel",
                "Other",
              ].map((jobPos) => (
                <MenuItem key={jobPos} value={jobPos}>
                  {jobPos}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Business title"
            value={businessTitle}
            onChange={(e) => setBusinessTitle(e.target.value)}
            helperText="Business representative's job title"
            required
            fullWidth
          />
          <A2PFormErrors messages={apiErrors} />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          {STOP_TEXT}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isLoading}
          onClick={handleNextButtonClicked}
          disabled={
            !firstName ||
            !lastName ||
            !email ||
            !phone ||
            !!phoneError ||
            !jobPosition ||
            !businessTitle
          }
        >
          Set up business address
        </LoadingButton>
      </DialogActions>
    </StepWrapper>
  )
}

function BusinessAddressStep({ setStepIndex, onClose }) {
  const [customerName, setCustomerName] = useState("")
  const [isoCountry, setISOCountry] = useState("")
  const [city, setCity] = useState("")
  const [region, setRegion] = useState("")
  const [street, setStreet] = useState("")
  const [postalCode, setPostalCode] = useState("")
  const [errors, setErrors] = useState([])
  const [apiErrors, setApiErrors] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const fullName = useSelector(selectFullName)
  const axios = useAuthorizedAxiosClient()
  const updateCRMSettingsAndGoNext = useUpdateCRMSettingsAndGoNext()

  function handleNextButtonClicked() {
    setIsLoading(true)
    axios
      .post(
        "/crm/api/twilio/a2p-10dlc/3-finalize-secondary-customer-profile/",
        {
          customer_name: customerName,
          iso_country: isoCountry,
          city,
          region,
          street,
          postal_code: postalCode,
        }
      )
      .then(({ data }) => {
        updateCRMSettingsAndGoNext(setStepIndex, data)
        setErrors([])
      })
      .catch((error) => {
        const results = error?.response?.data?.twilio_error
        setErrors(Array.isArray(results) ? results : [])
        setApiErrors(Array.isArray(results) ? [] : a2pErrorMessages(error))
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    setCustomerName(fullName)
  }, [fullName])

  return (
    <StepWrapper>
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <TextField
            label="Customer name"
            value={customerName}
            onChange={(e) => {
              setCustomerName(e.target.value)
              setErrors([])
            }}
            helperText="The name of the business (for the mailing address)"
            required
            fullWidth
          />
          <FormControl fullWidth size="small" required>
            <InputLabel id="label-iso-country">Country</InputLabel>
            <Select
              value={isoCountry}
              onChange={(e) => setISOCountry(e.target.value)}
              labelId="label-iso-country"
            >
              <MenuItem value="US">United States of America</MenuItem>
              <MenuItem value="CA">Canada</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="City"
            placeholder="San Francisco"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Region"
            placeholder="CA"
            helperText="Two-letter abbreviation for the state or province of the business address"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Street"
            helperText="The number and street of the business address"
            placeholder="23 Main Street"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Zip/Postal code"
            placeholder="90210"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            required
            fullWidth
          />
          <CustomerProfileErrors errors={errors} />
          <A2PFormErrors messages={apiErrors} />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          {STOP_TEXT}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isLoading}
          onClick={handleNextButtonClicked}
          disabled={
            !customerName ||
            !isoCountry ||
            !city ||
            !region ||
            !street ||
            !postalCode
          }
        >
          Set up messaging profile
        </LoadingButton>
      </DialogActions>
    </StepWrapper>
  )
}

function TrustProductStep({ setStepIndex, onClose }) {
  const [companyType, setCompanyType] = useState("")
  const [stockExchange, setStockExchange] = useState(null)
  const [stockExchangeInputValue, setStockExchangeInputValue] = useState("")
  const [stockTicker, setStockTicker] = useState("")
  const [errors, setErrors] = useState([])
  const [apiErrors, setApiErrors] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const axios = useAuthorizedAxiosClient()
  const updateCRMSettingsAndGoNext = useUpdateCRMSettingsAndGoNext()
  const PUBLIC_COMPANY_TYPE = "public"
  const companyIsPublic = companyType === PUBLIC_COMPANY_TYPE

  function handleCompanyTypeChanged(e) {
    const newCompanyType = e.target.value
    setCompanyType(newCompanyType)
    if (newCompanyType !== PUBLIC_COMPANY_TYPE) {
      setStockExchange(null)
      setStockTicker("")
    }
  }

  function handleNextButtonClicked() {
    setIsLoading(true)
    setApiErrors([])
    axios
      .post("/crm/api/twilio/a2p-10dlc/4-trust-product/", {
        company_type: companyType,
        stock_exchange: stockExchange || null,
        stock_ticker: stockTicker || null,
      })
      .then(({ data }) => updateCRMSettingsAndGoNext(setStepIndex, data))
      .catch((error) => {
        const results = error?.response?.data?.twilio_error
        setErrors(Array.isArray(results) ? results : [])
        setApiErrors(Array.isArray(results) ? [] : a2pErrorMessages(error))
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <StepWrapper>
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <FormControl fullWidth size="small" required>
            <InputLabel id="label-company-type">Company type</InputLabel>
            <Select
              value={companyType}
              onChange={handleCompanyTypeChanged}
              labelId="label-company-type"
            >
              {["government", "non-profit", "private", PUBLIC_COMPANY_TYPE].map(
                (companyType) => (
                  <MenuItem key={companyType} value={companyType}>
                    {capitalize(companyType)}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <Autocomplete
            label="Stock exchange"
            options={STOCK_EXCHANGES}
            value={stockExchange}
            onChange={(e, newValue) => setStockExchange(newValue)}
            inputValue={stockExchangeInputValue}
            onInputChange={(e, newValue) =>
              setStockExchangeInputValue(newValue)
            }
            renderInput={(props) => (
              <TextField label="Stock exchange" {...props} />
            )}
            fullWidth
            disabled={!companyIsPublic}
          />
          <TextField
            label="Stock ticker"
            value={stockTicker}
            onChange={(e) => setStockTicker(e.target.value)}
            fullWidth
            required={!!stockExchange}
            disabled={!companyIsPublic}
          />
          <TwilioErrors errors={errors} />
          <A2PFormErrors messages={apiErrors} />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          {STOP_TEXT}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleNextButtonClicked}
          loading={isLoading}
          disabled={!companyType || !!(stockExchange && !stockTicker)}
        >
          Submit and register brand
        </LoadingButton>
      </DialogActions>
    </StepWrapper>
  )
}

function RegisterBrandStep({ setStepIndex, onClose }) {
  const [attemptNo, setAttemptNo] = useState(0)
  const [errors, setErrors] = useState([])
  const axios = useAuthorizedAxiosClient()
  const dispatch = useDispatch()

  useEffect(
    () => {
      let cancelled = false
      setErrors([])
      axios
        .post("/crm/api/twilio/a2p-10dlc/5-register-brand/")
        .then(({ data }) => {
          dispatch(updateCRMSettings(data))
          onClose(REGISTER_A2P_10DLC_DIALOG_CLOSE_REASONS.brandRegPending)
        })
        .catch((error) => {
          if (cancelled) {
            return
          }
          if (error?.response?.status === 429 && attemptNo < 10) {
            setTimeout(() => setAttemptNo((n) => n + 1), 1500)
          } else {
            setErrors(a2pErrorMessages(error))
          }
        })
      return () => {
        cancelled = true
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attemptNo]
  )

  return (
    <StepWrapper>
      <DialogContent>
        <Column
          sx={{
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          {errors.length === 0 ? (
            <>
              <CircularProgress
                variant="indeterminate"
                color="primary"
                size={64}
              />
              <Column sx={{ gap: 1, textAlign: "center" }}>
                <Typography variant="h4">Registering Brand</Typography>
                <Typography variant="h6">Please wait...</Typography>
              </Column>
            </>
          ) : (
            <>
              <A2PFormErrors messages={errors} />
              <Button
                variant="contained"
                onClick={() => setAttemptNo((n) => n + 1)}
              >
                Try again
              </Button>
            </>
          )}
        </Column>
      </DialogContent>
    </StepWrapper>
  )
}

/*
  Brand to be Approved before this
*/
function CreateA2PCampaignStep({ setStepIndex, onClose }) {
  const [useCase, setUseCase] = useState(null)
  const [useCaseInputValue, setUseCaseInputValue] = useState("")
  const [useCaseOptions, setUseCaseOptions] = useState([])
  const [description, setDescription] = useState("")
  const [messageSamples, setMessageSamples] = useState([
    new MessageSample(),
    new MessageSample(),
  ])
  const [messageFlow, setMessageFlow] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [createCampaignError, setCreateCampaignError] = useState(null)
  const descriptionHasLengthError = textHasLengthError(description, 40, 4096)
  const messageFlowHasLengthError = textHasLengthError(messageFlow, 40, 2049)
  const axios = useAuthorizedAxiosClient()
  const dispatch = useDispatch()

  function handleSampleMessageChanged(e, index) {
    const message = e.target.value
    let error = null
    if (textLengthOutOfBounds(message, 20, 1024)) {
      error = "Message sample must be between 20 and 1024 characters"
    }
    setMessageSamples(
      messageSamples.map((messageSample, i) =>
        i === index ? new MessageSample(message, error) : messageSample
      )
    )
  }

  function handleSampleMessageDeleted(index) {
    setMessageSamples(messageSamples.filter((messageSample, i) => i !== index))
  }

  function handleAddSampleMessageButtonClicked() {
    setMessageSamples([...messageSamples, new MessageSample()])
  }

  function textHasLengthError(text: String, minLength, maxLength) {
    return text.length > 0 && textLengthOutOfBounds(text, minLength, maxLength)
  }

  function textLengthOutOfBounds(text: String, lowerBound, higherBound) {
    return text.length < lowerBound || text.length > higherBound
  }

  function handleCreateCampaignButtonClicked() {
    setIsLoading(true)
    axios
      .post("/crm/api/twilio/a2p-10dlc/7-create-a2p-campaign/", {
        description,
        us_app_to_person_usecase: useCase.code,
        message_samples: messageSamples
          .filter((messageSample) => messageSample.text.length > 0)
          .map((messageSample) => messageSample.text),
        message_flow: messageFlow,
      })
      .then(({ data }) => {
        dispatch(updateCRMSettings(data))
        onClose(REGISTER_A2P_10DLC_DIALOG_CLOSE_REASONS.createCampaignFinished)
      })
      .catch((error) =>
        setCreateCampaignError(
          error?.response?.data?.error || a2pErrorMessages(error).join(" ")
        )
      )
      .finally(() => setIsLoading(false))
  }

  useEffect(
    () => {
      axios
        .post("/crm/api/twilio/a2p-10dlc/6-campaign-use-cases/")
        .then(({ data }) => {
          setUseCaseOptions(data)
          if (Array.isArray(data) && data.length === 1) {
            setUseCase(data[0])
          }
        })
        .catch((error) =>
          setCreateCampaignError(a2pErrorMessages(error).join(" "))
        )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <StepWrapper>
      <DialogContent>
        <Column sx={{ gap: 3 }}>
          <Autocomplete
            value={useCase}
            onChange={(e, newValue) => setUseCase(newValue)}
            inputValue={useCaseInputValue}
            onInputChange={(e, newValue) => setUseCaseInputValue(newValue)}
            options={useCaseOptions}
            renderInput={(props) => (
              <TextField label="Campaign use case" required {...props} />
            )}
            renderOption={(props, option, state, ownerState) => (
              <Column
                sx={{ p: 2, alignItems: "flex-start!important" }}
                {...props}
              >
                <Typography variant="body2">
                  {ownerState.getOptionLabel(option)}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {option.description}
                </Typography>
              </Column>
            )}
            getOptionLabel={(option) => option.name}
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!descriptionHasLengthError}
            helperText={
              descriptionHasLengthError
                ? "Must be between 40 and 4096 characters"
                : "The description should include who the sender is, who the recipients are, and why messages are being sent to the recipients."
            }
            placeholder="This Campaign will send weekly marketing messages about sales and offers from Acme Sandwich Company to end customers who have opted in"
            multiline
            rows={3}
            required
          />
          <Column sx={{ gap: 2 }}>
            <Column sx={{ gap: 1 }}>
              <Typography variant="h6">Message samples</Typography>
              <Box component="ol" sx={{ margin: 0, textIndent: -1 }}>
                {[
                  "Messages that are representative of the Campaign's messages.",
                  "Must be clearly aligned with the Campaign description and usecase.",
                  "The Brand must be identified by name and/or website in each message.",
                  "Use square brackets [] to indicate variable content.",
                  "If the Campaign's messages will include links or phone numbers, include them in the sample messages.",
                ].map((tip, i) => (
                  <Typography
                    variant="body1"
                    component="li"
                    color="text.secondary"
                    key={i}
                  >
                    {tip}
                  </Typography>
                ))}
              </Box>
            </Column>
            {messageSamples.map((messageSample, i) => (
              <Column key={i}>
                {messageSamples.length > 2 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleSampleMessageDeleted(i)}
                    sx={{ alignSelf: "flex-end" }}
                  >
                    <CloseOutlinedIcon sx={{ fontSize: "15px" }} />
                  </IconButton>
                )}
                <TextField
                  label="Sample message"
                  value={messageSample.text}
                  onChange={(e) => handleSampleMessageChanged(e, i)}
                  error={!!messageSample.error}
                  helperText={messageSample.error}
                  placeholder="This is a message from the Acme Sandwich Company. Your order for [sandwich type, other item] will be delivered by [time] on [date]. If you have questions or would like to change your order schedule, please call 333-444-1212. If you would like to opt out of future notifications like this, text STOP in reply to this message."
                  multiline
                  rows={5}
                  fullWidth
                />
              </Column>
            ))}
            {messageSamples.length < 5 && (
              <IconButton
                onClick={handleAddSampleMessageButtonClicked}
                sx={{ alignSelf: "flex-end" }}
              >
                <AddOutlinedIcon />
              </IconButton>
            )}
          </Column>
          <Divider />
          <TextField
            label="Message flow"
            value={messageFlow}
            onChange={(e) => setMessageFlow(e.target.value)}
            error={messageFlowHasLengthError}
            helperText={
              messageFlowHasLengthError
                ? "Must be between 40 and 2049 characters"
                : "A detailed description of how end users opt in (consent) to receiving the Campaign's messages."
            }
            placeholder="End users opt-in by visiting www.example.com and adding their phone number. They then check a box agreeing to receive text messages from Acme, Inc. Additionally, end users can also opt-in by texting START to (111) 555-3333 to opt in. Terms and Conditions at www.example.com/tc. Privacy Policy at www.example.com/privacy"
            multiline
            rows={6}
            fullWidth
          />
          {createCampaignError && (
            <Typography variant="body1" color="error.main">
              <strong>{createCampaignError}</strong>
            </Typography>
          )}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          {STOP_TEXT}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={handleCreateCampaignButtonClicked}
          loading={isLoading}
          disabled={
            !useCase ||
            !description ||
            descriptionHasLengthError ||
            messageSamples.filter((ms) => !!ms.error).length > 0 ||
            messageSamples.filter((ms) => ms.text.length === 0).length > 0 ||
            !messageFlow ||
            messageFlowHasLengthError
          }
        >
          Create campaign and finish
        </LoadingButton>
      </DialogActions>
    </StepWrapper>
  )
}

function StepWrapper({ children }) {
  return (
    <Fade in>
      <Box>{children}</Box>
    </Fade>
  )
}

function CustomerProfileErrors({ errors }) {
  return (
    <Column sx={{ gap: 2 }}>
      {errors
        .filter((error) => !error.passed)
        .map((error, i) => (
          <Column key={i} sx={{ gap: 1 }}>
            <Typography variant="h6" color="error.main">
              {error.requirement_friendly_name}
            </Typography>
            <Box component="ol" sx={{ margin: 0 }}>
              {error.fields
                .filter((field) => !field.passed)
                .map((field, j) => (
                  <Typography key={j} component="li">
                    <strong>{field.friendly_name}</strong>{" "}
                    {field.failure_reason}
                  </Typography>
                ))}
            </Box>
          </Column>
        ))}
    </Column>
  )
}

function TwilioErrors({ errors }) {
  const errorsWithInvalid = errors.filter((result) => result.invalid.length > 0)
  return (
    errorsWithInvalid.length > 0 && (
      <ul style={{ margin: 0 }}>
        {errors.map((error) =>
          error.invalid.map((invalid, i) => (
            <li key={i}>
              <Typography
                variant="body1"
                component="span"
                color="error.main"
                sx={{ fontWeight: "bold" }}
              >
                {invalid.failure_reason}
              </Typography>
            </li>
          ))
        )}
      </ul>
    )
  )
}

function useUpdateCRMSettingsAndGoNext() {
  const dispatch = useDispatch()

  return (setStepIndex, data) => {
    dispatch(updateCRMSettings(data))
    setStepIndex((stepIndex) => stepIndex + 1)
  }
}

const STANDARD_STEPS = [
  { title: "Register Your Business for Texting", component: IntroStep },
  { title: "Your Business EIN", component: EinQuestionStep },
  {
    title: "Set Up Business Details",
    component: InitiateSecondaryCustomerProfileStep,
  },
  {
    title: "Set Up Business Representative Details",
    component: RepresentativeEndUserStep,
  },
  { title: "Set Up Business Address", component: BusinessAddressStep },
  { title: "Set Up Trust Product", component: TrustProductStep },
  { title: "Registering Brand", component: RegisterBrandStep },
  { title: "Create A2P Campaign", component: CreateA2PCampaignStep },
]

const SOLE_PROP_STEPS = [
  { title: "Register Your Business for Texting", component: IntroStep },
  { title: "Your Business EIN", component: EinQuestionStep },
  { title: "Sole Proprietor Details", component: SolePropStep },
  { title: "Registering Brand", component: RegisterBrandStep },
  { title: "Create A2P Campaign", component: CreateA2PCampaignStep },
]

/** Twilio evaluation results (twilio_error) or a plain API error, as text. */
function twilioOrApiErrors(error) {
  const results = error?.response?.data?.twilio_error
  if (Array.isArray(results) && results.length) {
    return results
      .filter((result) => !result.passed)
      .flatMap((result) =>
        (result.fields || [])
          .filter((field) => !field.passed)
          .map(
            (field) =>
              `${field.friendly_name || result.requirement_friendly_name}: ${
                field.failure_reason
              }`
          )
      )
  }
  return a2pErrorMessages(error)
}

const STOCK_EXCHANGES = [
  "AMEX",
  "AMX",
  "ASX",
  "B3",
  "BME",
  "BSE",
  "FRA",
  "ICEX",
  "JPX",
  "JSE",
  "KRX",
  "LON",
  "NASDAQ",
  "NONE",
  "NYSE",
  "NSE",
  "OMX",
  "OTHER",
  "SEHK",
  "SGX",
  "SSE",
  "STO",
  "SWX",
  "SZSE",
  "TSX",
  "TWSE",
  "VSE",
]

const STOP_TEXT = "I'll continue later"

class MessageSample {
  constructor(text = "", error = null) {
    this.text = text
    this.error = error
  }
}

export const REGISTER_A2P_10DLC_DIALOG_CLOSE_REASONS = {
  brandRegPending: 1,
  createCampaignFinished: 2,
}
